import {
    AgentMessageObject, AiHistoryMessage,
    BotMessageObject,
    BotMessageSendObject, FeedbackRatingMessage, FeedbackReasonMessage,
    OutputMessageObject,
    SummaryMessage
} from "../../types/chat";
import {ChatType, FeedbackRating, FeedbackReason, MessageSendState} from "../../types/enums";
import UserData from "../user/UserData";
import {HtmlToMarkdownConverter} from "../utils/HtmlToMarkdownConverter";
import {IHistoryMessageState} from "../chat/components/feedbackComponent";

export class MessageFactory {

    private static sendMessageCounter: number = 1;

    static create(
        chatType: string,
        message: string,
        trademark: string,
        className: string = 'Message',
        speechToText: boolean = false
    ): BotMessageObject | AgentMessageObject {

        const nowTimestamp = Date.now();
        const createdAt = new Date(nowTimestamp);
        const count = ++MessageFactory.sendMessageCounter;
        let messageId = 'msg-' + nowTimestamp.toString() + '-' + count.toString();

        if (typeof self.crypto?.randomUUID === 'function') {
            messageId = self.crypto.randomUUID();
        }

        if(chatType === ChatType.BOT_CHAT) {
            return {
                class: className,
                type: "Message",
                messageId: messageId,
                messageText: message.trim(),
                speechToText: speechToText,
                trademark: trademark,
                client: '',
                createdAt: createdAt.toISOString()
            };
        }

        return {
            class: className,
            type: 'Message',
            messageId: messageId,
            text: message.trim(),
            time: createdAt.toISOString(),
            speechToText: speechToText
        };
    }
    static createBotSendMessage(
        message: BotMessageObject,
        userData: UserData
    ): BotMessageSendObject {

        return {
            class: message.class,
            type: message.type,
            messageId: message.messageId,
            sessionId: userData.getUserSessionId(),
            userChatSessionId: userData.getUserChatSessionId(),
            messageText: message.messageText,
            speechToText: message.speechToText,
            trademark: message.trademark,
            client: message.client,
            subscriberId: userData.getSubscriberId(),
            createdAt: message.createdAt
        };
    }

    static createSummary(
        summary: string
    ): SummaryMessage {
        return {
            class: 'AiSummary',
            type: 'Message',
            summary: summary
        };
    }

    static createAiHistoryMessages(
        message: OutputMessageObject,
        converter: HtmlToMarkdownConverter,
        maxTextLength: number
    ): AiHistoryMessage[] {

        let aiMessageType = message.type == 'question' ? 'Human' : 'Bot';
        let convertedText = converter.toMarkdown(message.text);

        if(convertedText.length <= maxTextLength) {
            return [{
                class: 'AiHistory',
                type: 'Message',
                text: convertedText,
                originatingEntity: aiMessageType,
                current: 1,
                count: 1,
                isLastMessage: false
            }];
        }

        let arrayOfStrings = this.getTextAsArrayOfStrings(convertedText, maxTextLength);

        let aiHistoryMessages = [];
        for (let i = 0; i < arrayOfStrings.length; i++) {
            aiHistoryMessages.push({
                class: 'AiHistory',
                type: 'Message',
                text: arrayOfStrings[i].trim(),
                originatingEntity: aiMessageType,
                current: i+1,
                count: arrayOfStrings.length,
                isLastMessage: false
            });
        }

        return aiHistoryMessages;
    }

    private static getTextAsArrayOfStrings(text: string, maxStringLength: number): string[]
    {
        let textSplit = text.split(/\n/).filter(function (el: string) {
            return el != "";
        });

        let string = "";
        let arrayOfStrings: string[] = [];

        for (let i = 0; i < textSplit.length; i++) {
            let newString = string + textSplit[i] + '\n';

            if(newString.length <= maxStringLength) {
                if(i === textSplit.length-1) {
                    arrayOfStrings.push(newString);
                } else {
                    string = newString;
                }
            } else {
                if(string !== "") {
                    arrayOfStrings.push(string);
                }

                if((textSplit[i] + '\n').length <= maxStringLength) {
                    if(i === textSplit.length-1) {
                        arrayOfStrings.push(textSplit[i]);
                    } else {
                        string = textSplit[i] + '\n';
                    }
                } else {
                    // zufällige Aufteilung
                    string = "";
                    let match = textSplit[i].match(new RegExp("[\\s\\S]{1,"+maxStringLength+"}", "g"));
                    if(match !== null) {
                        for (let j = 0; j < match.length; j++) {
                            arrayOfStrings.push(match[j]);
                        }
                    }
                }
            }
        }

        return arrayOfStrings;
    }

    static createOutputMessage(
        text: string,
        id: string,
        chatType: ChatType,
        type: string,
        author: string,
        time: string,
        messageId: string,
        responseId: string,
        historyMessageState: IHistoryMessageState | null = null
    ): OutputMessageObject {
        let cssClass = '';
        switch (type) {
            case 'question':
                cssClass = 'chat-widget-message-type-question';
                break;
            case 'bot-question':
                cssClass = 'chat-widget-message-type-bot-question';
                break;
            case 'answer':
                if (chatType === ChatType.BOT_CHAT) {
                    cssClass = 'chat-widget-message-type-bot-answer';
                } else {
                    cssClass = 'chat-widget-message-type-answer';
                }
                break;
            case 'bot-answer':
                cssClass = 'chat-widget-message-type-bot-answer';
                break;
            case 'bot-suggestion':
                cssClass = 'chat-widget-message-type-bot-suggestion';
                break;
            case 'system-connect':
                cssClass = 'chat-widget-message-type-system system-connect';
                break;
            case 'system-online':
                cssClass = 'chat-widget-message-type-system system-online';
                break;
        }

        return {
            id: id,
            text: text,
            type: type,
            cssClass: cssClass,
            author: author,
            time: time,
            messageId: messageId,
            responseId: responseId,
            status: MessageSendState.NONE,
            historyMessageState: historyMessageState,
        };
    }

    static createFeedbackRatingMessage(
        id: string,
        messageId: string,
        rating: FeedbackRating,
        trademark: string,
        client: string,
        subscriberId: number|null,
        sessionId: string,
        userSessionId: string
    ): FeedbackRatingMessage
    {
        return {
            type: 'FeedbackRating',
            id,
            messageId,
            rating,
            trademark,
            client,
            subscriberId,
            sessionId,
            userSessionId: userSessionId,
        };
    }

    static createFeedbackReasonMessage(
        id: string,
        messageId: string,
        reasonCode: FeedbackReason|null,
        freeText: string|null,
        trademark: string,
        client: string,
        subscriberId: number|null,
        sessionId: string,
        userSessionId: string
    ): FeedbackReasonMessage
    {
        return {
            type: 'FeedbackReason',
            id,
            messageId,
            reasonCode,
            freeText,
            trademark,
            client,
            subscriberId,
            sessionId,
            userSessionId: userSessionId,
        };
    }
}
